import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import client from '@/api/client';
import type { SupervisorDetail, SupervisorPermissionsDto, Hostel } from '@/api/types';
import { Plus, UserCheck, UserX, Trash2, ChevronDown, ChevronRight, Shield, Send, Building2 } from 'lucide-react';
import { SendNotificationModal } from '@/components/notifications/SendNotificationModal';
import type { SendTarget } from '@/components/notifications/SendNotificationModal';
import { sanitizePhoneInput } from '@/lib/utils';

const PERMISSION_LABELS: { key: keyof SupervisorPermissionsDto; labelKey: string; label: string }[] = [
  { key: 'permDashboard',       labelKey: 'owner.permDashboard',       label: 'Dashboard' },
  { key: 'permFloorManagement', labelKey: 'owner.permFloorManagement', label: 'Floor Management' },
  { key: 'permRoomManagement',  labelKey: 'owner.permRoomManagement',  label: 'Room Management' },
  { key: 'permBedManagement',   labelKey: 'owner.permBedManagement',   label: 'Bed Management' },
  { key: 'permTenantManagement',  labelKey: 'owner.permTenantManagement',  label: 'Tenant Management' },
  { key: 'permPaymentManagement', labelKey: 'owner.permPaymentManagement', label: 'Payment Management' },
  { key: 'permReports',           labelKey: 'owner.permReports',           label: 'Reports' },
  { key: 'permNotifications',     labelKey: 'owner.permNotifications',     label: 'Notifications' },
];

function PermissionsPanel({ supervisorId: _supervisorId, permissions, onSave, isSaving }: {
  supervisorId: string;
  permissions: SupervisorPermissionsDto;
  onSave: (perms: SupervisorPermissionsDto) => void;
  isSaving: boolean;
}) {
  const { t } = useTranslation();
  const [perms, setPerms] = useState<SupervisorPermissionsDto>(permissions);
  const toggle = (key: keyof SupervisorPermissionsDto) =>
    setPerms(p => ({ ...p, [key]: !p[key] }));

  return (
    <div className="border-t border-gray-100 px-4 py-4 bg-violet-50">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-violet-600 mb-3">{t('owner.permissions', 'Permissions')}</h3>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
        {PERMISSION_LABELS.map(({ key, labelKey, label }) => (
          <label key={key} className="flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={perms[key]}
              onChange={() => toggle(key)}
              className="rounded accent-violet-600"
            />
            <span className="text-sm text-gray-700">{t(labelKey, label)}</span>
          </label>
        ))}
      </div>
      <button
        onClick={() => onSave(perms)}
        disabled={isSaving}
        className="bg-violet-600 hover:bg-violet-700 text-white px-4 py-1.5 rounded-lg text-sm font-medium disabled:opacity-50"
      >
        {isSaving ? t('owner.saving', 'Saving…') : t('owner.savePermissions', 'Save Permissions')}
      </button>
    </div>
  );
}

function HostelAssignPanel({ supervisorId, assignedHostelIds, hostels, onToggle, pending }: {
  supervisorId: string;
  assignedHostelIds: string[];
  hostels: Hostel[];
  onToggle: (supervisorId: string, hostelId: string, assign: boolean) => void;
  pending: boolean;
}) {
  const { t } = useTranslation();
  const activeHostels = hostels.filter(h => h.status === 'ACTIVE');

  if (activeHostels.length === 0) {
    return (
      <div className="border-t border-gray-100 px-4 py-4 bg-blue-50">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-2">{t('owner.assignedHostels', 'Assigned Hostels')}</h3>
        <p className="text-sm text-gray-500">{t('owner.noActiveHostelsYet', 'No active hostels yet. Create and get a hostel approved first.')}</p>
      </div>
    );
  }

  return (
    <div className="border-t border-gray-100 px-4 py-4 bg-blue-50">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-blue-600 mb-3">{t('owner.assignedHostels', 'Assigned Hostels')}</h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {activeHostels.map(h => {
          const isAssigned = assignedHostelIds.includes(h.id);
          return (
            <label key={h.id} className="flex items-center gap-2 cursor-pointer select-none bg-white rounded-lg px-3 py-2 border border-gray-200 hover:border-blue-300 transition">
              <input
                type="checkbox"
                checked={isAssigned}
                disabled={pending}
                onChange={() => onToggle(supervisorId, h.id, !isAssigned)}
                className="rounded accent-blue-600"
              />
              <Building2 size={13} className="text-gray-400 shrink-0" />
              <span className="text-sm text-gray-800 truncate">{h.name}</span>
              {isAssigned && (
                <span className="ml-auto text-[10px] font-semibold text-blue-600 bg-blue-100 px-1.5 py-0.5 rounded shrink-0">
                  {t('owner.assigned', 'Assigned')}
                </span>
              )}
            </label>
          );
        })}
      </div>
    </div>
  );
}

type ExpandedPanel = 'permissions' | 'hostels' | null;

export function OwnerSupervisorsPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [expandedMap, setExpandedMap] = useState<Record<string, ExpandedPanel>>({});
  const [form, setForm] = useState({ fullName: '', email: '', phone: '', password: '' });
  const [addError, setAddError] = useState('');
  const [notifTarget, setNotifTarget] = useState<SendTarget | null>(null);

  const { data: supervisors = [], isLoading } = useQuery<SupervisorDetail[]>({
    queryKey: ['owner-supervisors'],
    queryFn: () => client.get('/api/owner/supervisors').then(r => r as unknown as SupervisorDetail[]),
  });

  const { data: hostels = [] } = useQuery<Hostel[]>({
    queryKey: ['owner-hostels-for-assignment'],
    queryFn: () => client.get('/api/hostels').then(r => r as unknown as Hostel[]),
  });

  const addMutation = useMutation({
    mutationFn: (body: typeof form) => client.post('/api/owner/supervisors', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['owner-supervisors'] });
      setShowAddForm(false);
      setForm({ fullName: '', email: '', phone: '', password: '' });
      setAddError('');
    },
    onError: (e: Error) => setAddError(e.message),
  });

  const toggleMutation = useMutation({
    mutationFn: (id: string) => client.patch(`/api/owner/supervisors/${id}/toggle-active`, {}),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['owner-supervisors'] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => client.delete(`/api/owner/supervisors/${id}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['owner-supervisors'] }),
  });

  const permsMutation = useMutation({
    mutationFn: ({ id, perms }: { id: string; perms: SupervisorPermissionsDto }) =>
      client.put(`/api/owner/supervisors/${id}/permissions`, perms),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['owner-supervisors'] }),
  });

  const hostelAssignMutation = useMutation({
    mutationFn: ({ supervisorId, hostelId, assign }: { supervisorId: string; hostelId: string; assign: boolean }) =>
      assign
        ? client.post(`/api/owner/supervisors/${supervisorId}/hostels/${hostelId}`, {})
        : client.delete(`/api/owner/supervisors/${supervisorId}/hostels/${hostelId}`),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['owner-supervisors'] }),
  });

  const setPanel = (id: string, panel: ExpandedPanel) =>
    setExpandedMap(prev => ({ ...prev, [id]: prev[id] === panel ? null : panel }));

  const f = (key: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm(p => ({ ...p, [key]: e.target.value }));

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-gray-900">{t('nav.supervisors')}</h1>
        <div className="flex items-center gap-2">
          {supervisors.length > 0 && (
            <button
              onClick={() => setNotifTarget({ type: 'broadcast' })}
              className="flex items-center gap-2 bg-teal-600 hover:bg-teal-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition"
            >
              <Send size={14} /> {t('owner.notifyAll', 'Notify All')}
            </button>
          )}
          <button
            onClick={() => { setShowAddForm(!showAddForm); setAddError(''); }}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-lg text-sm font-medium"
          >
            <Plus size={16} /> {t('owner.addSupervisor', 'Add Supervisor')}
          </button>
        </div>
      </div>

      {showAddForm && (
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
          <h2 className="font-semibold text-gray-800 mb-4">{t('owner.newSupervisorAccount', 'New Supervisor Account')}</h2>
          <form
            onSubmit={e => { e.preventDefault(); addMutation.mutate(form); }}
            className="grid grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('auth.fullName')} *</label>
              <input required value={form.fullName} onChange={f('fullName')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.email')} *</label>
              <input required type="email" value={form.email} onChange={f('email')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('common.phone')}</label>
              <input type="tel" inputMode="tel" value={form.phone} onChange={e => setForm(p => ({ ...p, phone: sanitizePhoneInput(e.target.value) }))}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">{t('owner.temporaryPassword', 'Temporary Password')} *</label>
              <input required type="password" value={form.password} onChange={f('password')}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-violet-500" />
            </div>
            {addError && <p className="col-span-2 text-red-600 text-sm">{addError}</p>}
            <div className="col-span-2 flex gap-2">
              <button type="submit" disabled={addMutation.isPending}
                className="bg-violet-600 hover:bg-violet-700 text-white px-5 py-2 rounded-lg text-sm font-medium disabled:opacity-50">
                {addMutation.isPending ? t('owner.creating', 'Creating…') : t('owner.createSupervisor', 'Create Supervisor')}
              </button>
              <button type="button" onClick={() => setShowAddForm(false)}
                className="border px-5 py-2 rounded-lg text-sm">{t('common.cancel')}</button>
            </div>
          </form>
        </div>
      )}

      {isLoading ? (
        <p className="text-gray-400">{t('common.loading')}</p>
      ) : supervisors.length === 0 ? (
        <div className="bg-gray-50 rounded-xl p-12 text-center">
          <Shield size={40} className="mx-auto text-gray-300 mb-3" />
          <p className="font-medium text-gray-600 mb-1">{t('owner.noSupervisorsYet', 'No supervisors yet')}</p>
          <p className="text-sm text-gray-400">{t('owner.createSupervisorAccountsHint', 'Create supervisor accounts to delegate hostel management.')}</p>
        </div>
      ) : (
        <div className="space-y-3">
          {supervisors.map(s => {
            const expanded = expandedMap[s.id] ?? null;
            return (
              <div key={s.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="flex items-center gap-4 p-4">
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold shrink-0 ${s.isActive ? 'bg-violet-600' : 'bg-gray-400'}`}>
                    {s.fullName.charAt(0).toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">{s.fullName}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {s.email}{s.phone ? ` · ${s.phone}` : ''}
                      {s.assignedHostelIds?.length > 0 && (
                        <span className="ml-2 text-blue-600">· {t('owner.hostelCount', '{{count}} hostels', { count: s.assignedHostelIds.length })}</span>
                      )}
                    </p>
                  </div>
                  <span className={`shrink-0 px-2 py-0.5 rounded-full text-xs font-medium ${s.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}`}>
                    {s.isActive ? t('common.active') : t('common.inactive')}
                  </span>
                  <button
                    onClick={() => setNotifTarget({ type: 'user', userId: s.id, name: s.fullName })}
                    title={t('owner.sendNotificationToSupervisor', 'Send notification to this supervisor')}
                    className="shrink-0 p-1.5 rounded-lg hover:bg-teal-50 text-gray-400 hover:text-teal-600 transition"
                  >
                    <Send size={15} />
                  </button>
                  <button
                    onClick={() => setPanel(s.id, 'hostels')}
                    className={`shrink-0 flex items-center gap-1 text-xs font-medium border px-2.5 py-1.5 rounded-lg transition ${
                      expanded === 'hostels'
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'text-blue-600 hover:text-blue-700 border-blue-200'
                    }`}
                  >
                    <Building2 size={12} />
                    {t('nav.hostels')}
                    {expanded === 'hostels' ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                  <button
                    onClick={() => setPanel(s.id, 'permissions')}
                    className={`shrink-0 flex items-center gap-1 text-xs font-medium border px-2.5 py-1.5 rounded-lg transition ${
                      expanded === 'permissions'
                        ? 'bg-violet-600 text-white border-violet-600'
                        : 'text-violet-600 hover:text-violet-700 border-violet-200'
                    }`}
                  >
                    <Shield size={12} />
                    {t('owner.permissions', 'Permissions')}
                    {expanded === 'permissions' ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                  </button>
                  <button
                    onClick={() => toggleMutation.mutate(s.id)}
                    disabled={toggleMutation.isPending}
                    title={s.isActive ? t('owner.suspendSupervisor', 'Suspend supervisor') : t('owner.activateSupervisor', 'Activate supervisor')}
                    className={`shrink-0 p-1.5 rounded-lg transition ${s.isActive ? 'hover:bg-red-50 text-gray-400 hover:text-red-500' : 'hover:bg-green-50 text-gray-400 hover:text-green-500'}`}
                  >
                    {s.isActive ? <UserX size={16} /> : <UserCheck size={16} />}
                  </button>
                  <button
                    onClick={() => { if (confirm(t('owner.deleteSupervisorConfirm', 'Delete supervisor "{{name}}"?', { name: s.fullName }))) deleteMutation.mutate(s.id); }}
                    className="shrink-0 p-1.5 rounded-lg hover:bg-red-50 text-gray-400 hover:text-red-500 transition"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>

                {expanded === 'hostels' && (
                  <HostelAssignPanel
                    supervisorId={s.id}
                    assignedHostelIds={s.assignedHostelIds ?? []}
                    hostels={hostels}
                    onToggle={(supId, hostelId, assign) =>
                      hostelAssignMutation.mutate({ supervisorId: supId, hostelId, assign })
                    }
                    pending={hostelAssignMutation.isPending}
                  />
                )}

                {expanded === 'permissions' && (
                  s.permissions ? (
                    <PermissionsPanel
                      supervisorId={s.id}
                      permissions={s.permissions}
                      onSave={perms => permsMutation.mutate({ id: s.id, perms })}
                      isSaving={permsMutation.isPending}
                    />
                  ) : (
                    <div className="border-t border-gray-100 px-4 py-3 bg-amber-50 text-sm text-amber-700">
                      {t('owner.noPermissionsRecord', 'No permissions record found. Contact support.')}
                    </div>
                  )
                )}
              </div>
            );
          })}
        </div>
      )}

      {notifTarget && (
        <SendNotificationModal
          target={notifTarget}
          recipientLabel={t('owner.allSupervisors', 'All Supervisors')}
          onClose={() => setNotifTarget(null)}
        />
      )}
    </div>
  );
}
